import React, { useEffect, useMemo, useState } from "react";
import { Barcode, Trash2, X, Printer, PackageCheck, ShoppingBag, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ItemizeDialog } from "@/pages/admin/masterbills/ItemizeDialog";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";

// PiecesDialog — the physical children of ONE Product Master (design).
// Under Source-Linked Itemization a piece can only be created FROM a Master Bill
// (supplier / karigar / opening balance). Each piece keeps its bill link + its own
// purchase cost for life; sold pieces are locked to the invoice that sold them.
const STATUS_BADGE = {
  in_stock: "bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7]",
  sold: "bg-[#F4EBDD] text-[#8E6F33] border border-[#E6D3AA]",
  reserved: "bg-[#EDE7FB] text-[#4A2E9A] border border-[#C8B8F0]",
  returned: "bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9]",
  scrapped: "bg-[#F1E9E9] text-[#5C4444] border border-[#D8C6C6]",
  issued_to_karigar: "bg-[#E3EEFB] text-[#1F3F7A] border border-[#B9CCE7]",
};
const SOURCE_BADGE = {
  supplier: "bg-[#FFF7E6] text-[#6A4B12] border border-[#E7D0A8]",
  karigar: "bg-[#EEF3F7] text-[#1E3E5C] border border-[#C7D7E6]",
  opening: "bg-[#F4EBDD] text-[#8E6F33] border border-[#E6D3AA]",
};
// Manual status moves (sold is controlled by invoices only).
const STATUS_OPTIONS = ["in_stock", "reserved", "returned", "scrapped", "issued_to_karigar"];

export function PiecesDialog({ open, onOpenChange, product, onStockChanged }) {
  const [pieces, setPieces] = useState([]);
  const [openBills, setOpenBills] = useState([]);
  const [billId, setBillId] = useState("");
  const [itemizeOpen, setItemizeOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const toggleOne = (id) => setSelected((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const allSelected = useMemo(() => pieces.length > 0 && pieces.every((p) => selected.has(p.id)), [pieces, selected]);
  const someSelected = useMemo(() => selected.size > 0 && !allSelected, [selected, allSelected]);
  const toggleAll = () => setSelected(() => allSelected ? new Set() : new Set(pieces.map((p) => p.id)));

  const load = async () => {
    if (!product?.id) return;
    setLoading(true);
    try {
      const [{ data }, { data: bills }] = await Promise.all([
        api.get(`/inventory/products/${product.id}/pieces`),
        api.get("/inventory/master-bills/open"),
      ]);
      setPieces(data.pieces || []);
      setOpenBills(bills.bills || []);
      setSelected(new Set());
    } catch (e) { toast.error("Failed to load pieces"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) { setBillId(""); setSelected(new Set()); load(); }
    // eslint-disable-next-line
  }, [open, product?.id]);

  const selectedBill = useMemo(() => openBills.find((b) => b.id === billId) || null, [openBills, billId]);
  const inStockCount = pieces.filter((p) => p.status === "in_stock").length;

  const updateStatus = async (piece, status) => {
    try {
      await api.patch(`/inventory/pieces/${piece.id}`, { status });
      toast.success(`Piece ${piece.barcode} → ${status.replace(/_/g, " ")}`);
      await load(); onStockChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const deletePiece = async (piece) => {
    if (!window.confirm(`Remove piece ${piece.barcode}? Its weight, carats and cost go back to ${piece.master_bill_no || "its bill"}'s unassigned pool.`)) return;
    try {
      await api.delete(`/inventory/pieces/${piece.id}`);
      toast.success(`Piece ${piece.barcode} removed — returned to ${piece.master_bill_no || "bill"} pool`);
      await load(); onStockChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const printAll = () => {
    if (!pieces.length) { toast.error("No pieces to print"); return; }
    window.open(`/admin/print-tags?barcodes=${encodeURIComponent(pieces.map((p) => p.barcode).join(","))}`, "_blank", "noopener");
  };
  const printOne = (piece) => window.open(`/admin/print-tags?barcodes=${encodeURIComponent(piece.barcode)}`, "_blank", "noopener");
  const printSelected = () => {
    if (!selected.size) { toast.error("Tick at least one piece to print"); return; }
    const codes = pieces.filter((p) => selected.has(p.id)).map((p) => p.barcode).join(",");
    window.open(`/admin/print-tags?barcodes=${encodeURIComponent(codes)}`, "_blank", "noopener");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pieces-dialog" className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Barcode className="h-5 w-5 text-[hsl(var(--primary))]" /> Physical Pieces &amp; Barcodes</DialogTitle>
          <DialogDescription>
            {product?.name ? <><span className="font-medium">{product.name}</span> · SKU <span className="font-mono">{product.sku}</span></> : "Product"}
            {" — every piece is drawn from a Source Reference (purchase or opening balance) and keeps that source's cost for life."}
          </DialogDescription>
        </DialogHeader>

        {/* Add pieces - Source Reference is mandatory. The dropdown lists only OPEN
            Master Bills (pending purchases + opening balances); the actual itemization,
            weight/cost draw-down and over-allocation warnings are the shared ItemizeDialog. */}
        <Card className="p-4" data-testid="pieces-itemize-card">
          <div className="flex items-center gap-2 text-sm font-medium mb-3"><PackageCheck className="h-4 w-4 text-[hsl(var(--primary))]" /> Add pieces</div>
          {loading ? <Skeleton className="h-10 w-full" /> : openBills.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-[#F7F0E6] p-4 text-sm" data-testid="pieces-no-source">
              <div className="font-medium">No open source to draw pieces from.</div>
              <p className="text-xs text-muted-foreground mt-1">A Source Reference is required for every piece. Record the supplier <span className="font-medium text-foreground">Purchase</span> (its jewellery lines open a pending pool automatically) or use <span className="font-medium text-foreground">Import Opening Pieces</span> on the Inventory page for stock you already own.</p>
              <Button asChild size="sm" variant="secondary" className="mt-3 gap-1"><Link to="/admin/purchases" data-testid="pieces-goto-purchases"><ShoppingBag className="h-3.5 w-3.5" /> Go to Purchases <ArrowRight className="h-3.5 w-3.5" /></Link></Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
              <div className="min-w-0">
                <Label className="text-xs">Source Reference <span className="text-destructive" aria-hidden="true">*</span> <span className="text-muted-foreground font-normal">(open purchase / opening balance)</span></Label>
                <Select value={billId || "none"} onValueChange={(v) => setBillId(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="pieces-source-select" aria-required="true" className={`mt-1 h-10 ${!billId ? "border-[#E7D0A8]" : ""}`}><SelectValue placeholder="Select a source reference" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select a source reference…</SelectItem>
                    {openBills.map((b) => (
                      <SelectItem key={b.id} value={b.id} data-testid={`pieces-source-option-${b.bill_no}`}>
                        <span className="font-mono text-xs mr-2">{b.bill_no}</span>{b.source_name}{b.reference_no ? ` · ${b.reference_no}` : ""} · {b.unassigned_qty} pc left · {Number(b.unassigned_gold_weight_g || 0).toFixed(3)} g
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="pieces-itemize-button" className="gap-2 h-10" disabled={!selectedBill} onClick={() => setItemizeOpen(true)}><PackageCheck className="h-4 w-4" /> Itemize pieces</Button>
            </div>
          )}
          {!loading && openBills.length > 0 && !selectedBill && (
            <p className="text-xs text-muted-foreground mt-2" data-testid="pieces-source-required-hint">Choose the source reference first - pieces cannot be added without one.</p>
          )}
          {selectedBill && (
            <p className="text-xs text-muted-foreground mt-2">
              <span className="font-mono">{selectedBill.bill_no}</span> — {selectedBill.unassigned_qty} piece(s), {Number(selectedBill.unassigned_gold_weight_g || 0).toFixed(3)} g gold, {Number(selectedBill.unassigned_diamond_ct || 0).toFixed(2)} ct and {formatINR(selectedBill.unassigned_inr_cost)} still unassigned.
            </p>
          )}
        </Card>

        {/* Existing pieces */}
        <div>
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-sm font-medium">Pieces ({pieces.length}) <span className="text-xs text-muted-foreground font-normal">· {inStockCount} in stock</span>{selected.size > 0 && <span className="ml-2 text-xs text-muted-foreground">· {selected.size} selected</span>}</div>
            <div className="inline-flex items-center gap-2">
              <Button data-testid="print-selected-tags-button" size="sm" variant="outline" className="gap-1" onClick={printSelected} disabled={selected.size === 0}><Printer className="h-3.5 w-3.5" /> Print Selected {selected.size > 0 ? `(${selected.size})` : ""}</Button>
              <Button data-testid="print-all-tags-button" size="sm" variant="outline" className="gap-1" onClick={printAll} disabled={!pieces.length}><Printer className="h-3.5 w-3.5" /> Print All</Button>
            </div>
          </div>
          <div className="overflow-x-auto rounded-lg border border-border">
            <Table data-testid="pieces-table">
              <TableHeader>
                <TableRow className="bg-[#F4EBDD]">
                  <TableHead className="w-10"><Checkbox data-testid="pieces-select-all" checked={allSelected ? true : (someSelected ? "indeterminate" : false)} onCheckedChange={toggleAll} aria-label="Select all pieces" /></TableHead>
                  <TableHead>Barcode</TableHead>
                  <TableHead>Gross</TableHead>
                  <TableHead>Net</TableHead>
                  <TableHead>Dia</TableHead>
                  <TableHead>Purity</TableHead>
                  <TableHead>Source Reference</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={10} className="text-center py-6 text-muted-foreground">Loading...</TableCell></TableRow>}
                {!loading && pieces.length === 0 && (
                  <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground text-sm">No physical pieces yet — this design is <span className="font-medium text-foreground">Make to Order</span> until pieces are added from a source reference.</TableCell></TableRow>
                )}
                {pieces.map((pc) => (
                  <TableRow key={pc.id} data-testid={`piece-row-${pc.barcode}`} className={`hover:bg-[#F7F0E6] ${selected.has(pc.id) ? "bg-[#FBF3E4]" : ""}`}>
                    <TableCell className="w-10"><Checkbox data-testid="piece-select-checkbox" checked={selected.has(pc.id)} onCheckedChange={() => toggleOne(pc.id)} aria-label={`Select ${pc.barcode}`} /></TableCell>
                    <TableCell className="font-mono text-xs">
                      {pc.barcode}
                      {(pc.huid || pc.stone_certificate_no) && (
                        <div className="text-[10px] text-muted-foreground whitespace-nowrap" data-testid={`piece-cert-${pc.barcode}`}>
                          {pc.huid ? `HUID ${pc.huid}` : ""}{pc.huid && pc.stone_certificate_no ? " · " : ""}{pc.stone_certificate_no ? `Cert ${pc.stone_certificate_no}` : ""}
                        </div>
                      )}
                      {pc.notes ? <div className="text-[10px] text-muted-foreground truncate max-w-[160px]">{pc.notes}</div> : null}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{pc.gross_weight_g}g</TableCell>
                    <TableCell className="font-mono text-xs">{pc.net_weight_g}g</TableCell>
                    <TableCell className="font-mono text-xs">{pc.diamond_carat_total ? `${pc.diamond_carat_total}ct` : "—"}</TableCell>
                    <TableCell className="text-xs">{pc.purity || "—"}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      <span className="font-mono">{pc.master_bill_no || "—"}</span>
                      {pc.source_type && <Badge className={`${SOURCE_BADGE[pc.source_type] || ""} text-[10px] ml-1`}>{pc.source_type}</Badge>}
                      {pc.source_name && <div className="text-[10px] text-muted-foreground truncate max-w-[150px]">{pc.source_name}</div>}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-right">{formatINR(pc.unit_cost)}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_BADGE[pc.status] || ""}>{pc.status.replace(/_/g, " ")}</Badge>
                      {pc.status === "sold" && pc.sold_ref_no && <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{pc.sold_ref_no}</div>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        {pc.status === "sold" ? (
                          <span className="text-[11px] text-muted-foreground px-2" title="Sold status is controlled by the invoice">locked</span>
                        ) : (
                          <Select value={pc.status} onValueChange={(v) => updateStatus(pc, v)}>
                            <SelectTrigger className="h-8 w-[130px] text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s} className="text-xs">{s.replace(/_/g, " ")}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                        <Button data-testid="print-piece-tag-button" size="icon" variant="ghost" title="Print tag" onClick={() => printOne(pc)}><Printer className="h-4 w-4 text-[hsl(var(--primary))]" /></Button>
                        <Button data-testid="delete-piece-button" size="icon" variant="ghost" title={pc.status === "sold" ? "Sold pieces cannot be removed" : "Remove piece (back to bill pool)"} disabled={pc.status === "sold"} onClick={() => deletePiece(pc)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="gap-1"><X className="h-4 w-4" /> Close</Button>
        </DialogFooter>

        <ItemizeDialog open={itemizeOpen} onOpenChange={setItemizeOpen} bill={selectedBill} products={product ? [product] : []} lockedProductId={product?.id}
                       onDone={async () => { await load(); onStockChanged?.(); }} />
      </DialogContent>
    </Dialog>
  );
}
